import React, { ChangeEventHandler, type FC, Fragment, ReactNode } from "react";

import { classNames } from "@/shared/utils/classnames";

export type RadioButtonGroupOption = {
  key?: string | number;
  text?: string;
  description?: string;
  element?: ReactNode;
  value: any;
  highlight?: boolean;
};

export type RadioButtonGroupProps = {
  isLoading?: boolean;
  options: Array<RadioButtonGroupOption>;
  highlight: Array<RadioButtonGroupOption>;
  name: string;
  error?: string | undefined | any;
  onChange: ChangeEventHandler<HTMLInputElement>;
  label?: string;
  horizontal?: boolean;
  value: any;
};

export const RadioButtonGroup: FC<RadioButtonGroupProps> = ({
  options,
  onChange,
  isLoading,
  name,
  error,
  label,
  highlight = [],
  horizontal,
  value,
}) => {
  return (
    <div className="flex flex-col gap-2">
      <label className="block text-sm font-medium leading-6 text-gray-600">
        {label}
      </label>
      {isLoading ? (
        <Fragment>
          <ul
            className={classNames(
              "flex gap-4",
              horizontal ? "flex-row" : "flex-col"
            )}
          >
            {new Array(3).fill(1).map(() => (
              <li className="flex gap-4 items-center">
                <div className="rounded-full w-4 h-4 bg-gray-100 border-gray-300 border dark:bg-gray-700 dark:border-gray-600" />
                <div className="flex flex-col w-full text-sm font-medium text-gray-900 dark:text-gray-300 gap-2">
                  <div className="h-6 w-1/2 bg-gray-300 rounded-md animate-pulse" />
                  <div className="h-4 w-3/4 bg-gray-300 rounded-md animate-pulse" />
                </div>
              </li>
            ))}
          </ul>
        </Fragment>
      ) : (
        <ul
          className={classNames(
            "flex gap-4",
            horizontal ? "flex-row" : "flex-col"
          )}
        >
          {options?.map((option, index) => (
            <li className={`flex gap-4 items-center px-4 py-2 border-2 border-dashed rounded-md ${(highlight?.find((_) => _.value == option.value)) ? 'border-primary ' : 'border-transparent'} `}>
              <input
                id={`${name}-option-${index}`}
                type="radio"
                checked={options[index].value == value}
                onChange={(event) => {
                  if (typeof onChange != "function") return;
                  onChange({
                    ...event,
                    target: {
                      ...event.target,
                      value: option.value,
                      name: name,
                    },
                  });
                }}
                name={name}
                className="w-4 h-4 text-primary-500 bg-gray-100 border-gray-300 focus:ring-primary-500 dark:focus:ring-primary-500 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <label
                htmlFor={`${name}-option-${index}`}
                className="flex flex-col w-full text-sm font-medium text-gray-900 dark:text-gray-300"
              >
                {option.element && option.element}
                {option.text && (
                  <p className="text-md leading-6 text-gray-600">
                    {option.text}
                  </p>
                )}
                {option.description && (
                  <small className="text-sm font-light leading-6 text-gray-600">
                    {option.description}
                  </small>
                )}
              </label>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="text-red-500">{error}</p>}
    </div>
  );
};
